import { connect } from 'react-redux';
import { get, isEmpty } from 'lodash';
import i18next from 'i18next';
import React, { useEffect, useState } from 'react';

import { Input } from '@lib/inpulse/Input';

import { closeGenericModal } from '@actions/modal';
import { loading, loadingSuccess } from '@actions/loading';
import { showErrorMessage, showSuccessMessage } from '@actions/messageconfirmation';

import CreateIngredientModal from '@admin/components/CreateIngredientModal';

import { Dropdown, Tooltip } from '@commons/utils/styledLibraryComponents';
import { ENTITY_UNITS } from '@commons/constants/units';
import { formatNumber } from '@commons/DisplayNumber';
import { handleUnitsValidity } from '@commons/utils/units';
import Text, { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';

import { getClientInfo } from '@selectors/client';

import { getRecipeCost, getRecipesOfClient } from '@services/recipe';
import { ingredient as ingredientService } from '@services/ingredient';
import { supplierProduct as supplierProductService } from '@services/supplierProduct';
import centralService from '@services/central';
import clientService from '@services/client';

import { CATEGORY_TYPES_OBJECT, getPropertyNoneValue } from '@commons/constants/categoryTypes';

import theme from '@theme';

import {
  CompositionContainer,
  Container,
  DropdownsContainer,
  ExternalRedirectionContainer,
  InformationComponentsContainer,
  InformationContainer,
  LabelContainer,
  LinkImage,
  TitleContainer,
  GapContainer,
} from './styledComponents';
import IngredientDropdown from './components/IngredientDropdown';
import RecipeDropdown from './components/RecipeDropdown';

export const getCompositionTypes = () => [
  {
    value: i18next.t('GENERAL.NONE_VALUE'),
    id: 'NONE',
  },
  {
    value: i18next.t('GENERAL.RECIPE'),
    id: 'RECIPE',
  },
  {
    value: i18next.t('GENERAL.INGREDIENT'),
    id: 'INGREDIENT',
  },
];

const InformationComponent = ({ label, value, link, tooltipText }) => (
  <InformationContainer>
    <LabelContainer>
      <Text color={ENUM_COLORS.DARKER} font={ENUM_FONTS.TEXT_MIDDLE_NORMAL}>
        {label} :
      </Text>
      {!!link && (
        <LinkImage src="/images/inpulse/open-a-new-black-small.svg" onClick={link.onClick} />
      )}
      {!!tooltipText && !!tooltipText.length && (
        <div style={{ marginTop: '-2px' }}>
          <Tooltip displayBigger={true} text={tooltipText} />
        </div>
      )}
    </LabelContainer>
    <Text color={ENUM_COLORS.BLACK} font={ENUM_FONTS.TEXT_MIDDLE_BOLD}>
      {value}
    </Text>
  </InformationContainer>
);

const ProductComposition = (props) => {
  const {
    client: { clientId, defaultChannelId, clientChannels },
    pageLoading,
    pageLoaded,
    showErrorMessage,
    product,
    isReadOnly,
    currency: { alphabeticCode, numberDecimals },
    composition,
    compositionError,
    hasRecipeVariant = false,
    setComposition,
    selectedCompoType,
    setSelectedCompoType,
    onProductChange,
    isKitchenProduct = false,
    showSuccessMessage,
    closeGenericModal,
    supplierProduct,
  } = props;

  const compositionTypes = getCompositionTypes();

  const [isLoading, setIsLoading] = useState(true);
  const [warningMessage, setWarningMessage] = useState(null);

  const [recipes, setRecipes] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [showIngredientOrRecipeDropdown, setShowIngredientOrRecipeDropdown] = useState(true);
  const [portionSizeInGrams, setPortionSizeInGrams] = useState(
    get(product, 'portionSizeInGrams', null),
  );

  const [openCreateIngredientModal, setOpenCreateIngredientModal] = useState(false);

  // If we are in edition, it's the central kitchen SP that is associated to the central kitchen product
  // Else, it's the central kitchen SP that will be created and associated to the central kitchen product in creation
  const [productAssociatedSP, setProductAssociatedSP] = useState(null);

  useEffect(() => {
    if (!selectedCompoType || isEmpty(selectedCompoType)) {
      return;
    }

    const actionsToExecute = {
      NONE: () => setShowIngredientOrRecipeDropdown(false),
      RECIPE: fetchRecipes,
      INGREDIENT: fetchIngredients,
    };

    actionsToExecute[selectedCompoType.id]();
  }, [selectedCompoType, productAssociatedSP]);

  useEffect(() => {
    if (isEmpty(supplierProduct)) {
      return;
    }
    // Central Kitchen Product is already created
    if (supplierProduct.id) {
      // Need to fetch the associated SP details because supplierProduct from props does not have the SP conversions details here
      getAssociatedSPDetail(supplierProduct.id);
      return;
    }

    // Central Kitchen product is in creation and associated SP is not created yet but we have some SP details (packagings, conversions, ...)
    if (supplierProduct.packagingUnit) {
      setProductAssociatedSP(supplierProduct);
    }
  }, [supplierProduct]);

  useEffect(() => {
    if (isEmpty(productAssociatedSP)) {
      return;
    }

    // Triggered by central kitchen product edition
    if (selectedCompoType.id === 'INGREDIENT') {
      const formattedIngredientList = getFormattedIngredientsList(ingredients);

      setIngredients(formattedIngredientList);
      return;
    }

    const formattedRecipes = getFormattedRecipesList(recipes);
    setRecipes(formattedRecipes);
  }, [productAssociatedSP, selectedCompoType]);

  const getAssociatedSPDetail = async (supplierProductId) => {
    if (!supplierProductId) {
      return;
    }

    try {
      const supplierProductFetched = await supplierProductService.getSingleSupplierProduct(
        supplierProductId,
      );

      setProductAssociatedSP(supplierProductFetched);
    } catch {
      showErrorMessage(i18next.t('ADMIN.SUPPLIER_PRODUCTS.SINGLE_FETCH_ERROR'));
    }
  };

  const getFormattedIngredientsList = (ingredients) =>
    ingredients.map((ingredient) => {
      // If no SP associated to the product, all ingredients units are valid because we don't have anything to compare them to
      const isUnitValid = !productAssociatedSP
        ? true
        : handleUnitsValidity(
            ingredient.unit,
            get(productAssociatedSP, ['entity', 'unit'], null),
            get(productAssociatedSP, 'conversions', []),
            get(productAssociatedSP, 'packagings', []),
          );

      return {
        ...ingredient,
        isDisabled: !isUnitValid,
      };
    });

  const getFormattedRecipesList = (recipes) =>
    recipes.map((recipe) => {
      // If no SP associated to the product, all recipes units are valid because we don't have anything to compare them to
      const isUnitValid = !productAssociatedSP
        ? true
        : handleUnitsValidity(
            recipe.unit,
            get(productAssociatedSP, ['entity', 'unit'], null),
            get(productAssociatedSP, 'conversions', []),
            get(productAssociatedSP, 'packagings', []),
          );

      return {
        ...recipe,
        isDisabled: (!!recipe.product && product.entityId !== recipe.id) || !isUnitValid, // recipe is already associated or recipe do not have the same unit
        category: recipe.category || getPropertyNoneValue(),
      };
    });

  const getTotalCost = (composition) => {
    if (composition.isIngredient) {
      return `${formatNumber(composition.cost || null, numberDecimals)}`;
    }

    if (clientChannels.length <= 1 || isKitchenProduct) {
      const defaultChannelCost = composition.costByChannelId?.[defaultChannelId];

      return `${formatNumber(defaultChannelCost || null, numberDecimals)}`;
    }

    return clientChannels
      .map((channel) => {
        const cost = composition.costByChannelId?.[channel.id] || null;

        return formatNumber(cost, numberDecimals);
      })
      .join(' | ');
  };

  const getRatio = (cost, channelId) => {
    const priceHT = parseFloat(product.priceHTByChannelId?.[channelId]);

    const formattedCost = formatNumber(cost, numberDecimals);
    const formattedProductPrice = formatNumber(priceHT, numberDecimals);

    return formatNumber(
      formattedCost > 0 && !Number.isNaN(formattedProductPrice) && formattedProductPrice > 0
        ? (formattedCost / formattedProductPrice) * 100
        : null,
      numberDecimals,
    );
  };

  const fetchRecipes = async () => {
    if (recipes.length) {
      setShowIngredientOrRecipeDropdown(true);
      return;
    }

    try {
      const clientRecipes = isKitchenProduct
        ? await centralService.getKitchenRecipesOfClient(clientId)
        : await getRecipesOfClient();

      const activeRecipes = clientRecipes.filter(({ active }) => active);

      setRecipes(activeRecipes);
      setShowIngredientOrRecipeDropdown(true);
    } catch {
      showErrorMessage(i18next.t('ADMIN.RECIPES.COMPOSITION_FETCH_ERROR_PLURAL'));
      setShowIngredientOrRecipeDropdown(false);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchIngredients = async () => {
    if (ingredients.length) {
      setShowIngredientOrRecipeDropdown(true);
      return;
    }

    try {
      const clientIngredients = await ingredientService.getIngredients(false);

      // For Central kitchen products, we display all active ingredients of client
      const activeIngredients = clientIngredients.filter(
        ({ active, unit }) => active && (isKitchenProduct || unit === ENTITY_UNITS.UNIT),
      );

      // Disable ingredients that do not have the same unit
      setIngredients(activeIngredients);
      setShowIngredientOrRecipeDropdown(true);
    } catch {
      showErrorMessage(i18next.t('ADMIN.INGREDIENTS.FETCH_ERROR'));
      setShowIngredientOrRecipeDropdown(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isEmpty(product) || portionSizeInGrams === product.portionSizeInGrams) {
      return;
    }

    setPortionSizeInGrams(get(product, 'portionSizeInGrams', null));
  }, [product]);

  const handlePortionSizeInGramsChange = (value) => {
    if (value < 0) {
      return;
    }

    const formattedValue = parseInt(value);

    setPortionSizeInGrams(formattedValue);
    onProductChange({ ...product, portionSizeInGrams: formattedValue });
  };

  const handleCompositionTypeChange = (selectedType) => {
    setComposition({});
    setSelectedCompoType(selectedType);

    if (selectedType.id === 'NONE') {
      onProductChange({ ...product, entityId: null, portionSizeInGrams: null });
      setPortionSizeInGrams(null);
    }
    setWarningMessage(null);
  };

  const handleEntityChange = async (selectedEntity) => {
    pageLoading();
    try {
      const recipeCost = await getRecipeCost(selectedEntity.id, { withAllergens: false });

      selectedEntity.costByChannelId = recipeCost.costByChannelId;

      /**
       * Leftovers from legacy code, the fetches for:
       * - ingredients have a 'products' (array) property
       * - recipes have a 'product' (object) property
       */
      const isAlreadyMappedRecipe =
        selectedEntity.product && product.entityId !== selectedEntity.id;

      // Check if the entity is already mapped
      if (isAlreadyMappedRecipe) {
        setWarningMessage(i18next.t('ADMIN.PRODUCTS.ALREADY_MAPPED_RECIPE'));
      } else {
        setWarningMessage(null);
      }

      setComposition(selectedEntity);
      onProductChange({ ...product, entityId: selectedEntity.id, selectedEntity });
    } catch {
      showErrorMessage(i18next.t('ADMIN.PRODUCTS.CREATE_PRODUCT_MODAL_CALCULATE_COST_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const handleOpenEntityDetails = () => {
    if (selectedCompoType.id === 'RECIPE' && composition.isKitchen) {
      window.open(`/admin/kitchen-products/recipes/${composition.id}/details`, '_blank');
      return;
    }

    window.open(
      `/admin/products/${selectedCompoType.id === 'RECIPE' ? 'recipes' : 'ingredients'}/${
        composition.id
      }/details`,
      '_blank',
    );
  };

  const handleCallToCreateIngredient = async (ingredient) => {
    pageLoading();

    try {
      let ingredientCategoryId = ingredient.categoryId;

      if (!ingredientCategoryId) {
        const { categories } = await clientService.getCategoriesAndSubCategories(
          CATEGORY_TYPES_OBJECT.INGREDIENT,
        );

        const categoryNone = categories.find(({ name }) => !name);

        ingredientCategoryId = categoryNone.id;
      }

      const newlyCreatedIngredient = await ingredientService.createIngredient(clientId, {
        name: ingredient.name,
        unit: ingredient.unit,
        code: ingredient.code,
        cost: ingredient.cost,
        supplierProducts: ingredient.supplierProducts.map(({ id, isUsedForCost }) => ({
          id,
          isUsedForCost,
        })),
        allergenIds: ingredient.allergens.map((allergen) => allergen.id),
        isStrategic: ingredient.isStrategic,
        categoryId: ingredientCategoryId,
      });

      showSuccessMessage(i18next.t('ADMIN.INGREDIENTS.CREATION_SUCCESS'));

      await handleEntityChange(newlyCreatedIngredient);

      closeGenericModal();
    } catch {
      showErrorMessage(i18next.t('ADMIN.INGREDIENTS.CREATION_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  // Cost excl VAT tooltip and value
  const getCostExclVATToolTipAndValue = () => {
    const value = getTotalCost(composition);

    if (!isKitchenProduct && composition.hasMultipleChannels && clientChannels.length > 1) {
      return {
        tooltip: clientChannels
          .map((channel) => {
            const channelName = i18next.t(`${channel.translationKey}.FULL`);

            return `${i18next.t('ADMIN.RECIPES.DETAILS_COSTS_TOOLTIP_MULTIPLE_CHANNELS', {
              channelName,
            })}`;
          })
          .join(' | '),
        value,
      };
    }

    return {
      tooltip: null,
      value,
    };
  };

  // Price excl VAT tooltip and value
  const getPriceExclVATToolTipAndValue = () => {
    if (!isKitchenProduct && clientChannels.length > 1 && !composition.isIngredient) {
      return {
        tooltip: clientChannels
          .map((channel) => {
            const channelName = i18next.t(`${channel.translationKey}.FULL`);

            return `${i18next.t('GENERAL.PRICE_EX_TAX_WITH_CHANNEL', {
              channel: channelName,
            })}`;
          })
          .join(' | '),
        value: clientChannels
          .map((channel) =>
            formatNumber(product.priceHTByChannelId?.[channel.id] || null, numberDecimals),
          )
          .join(' | '),
      };
    }

    return {
      tooltip: null,
      value: formatNumber(product.priceHTByChannelId?.[defaultChannelId] || null, numberDecimals),
    };
  };

  // Ratio tooltip and value
  const getTotalRatio = (composition) => {
    if (composition.isIngredient) {
      const entityCost = composition.cost;
      const ratio = getRatio(entityCost, defaultChannelId);

      return `${ratio}${entityCost ? '%' : ''}`;
    }

    if (clientChannels.length <= 1 || isKitchenProduct) {
      const costDefaultChannel = composition.costByChannelId?.[defaultChannelId];
      const ratioDefaultChannel = getRatio(costDefaultChannel, defaultChannelId);

      return `${ratioDefaultChannel}${costDefaultChannel ? '%' : ''}`;
    }

    return clientChannels
      .map((channel) => {
        const cost = composition.costByChannelId?.[channel.id] || null;
        const ratio = getRatio(cost, channel.id);

        return `${ratio}${cost ? '%' : ''}`;
      })
      .join(' | ');
  };

  const getRatioToolTipAndValue = () => {
    const value = getTotalRatio(composition);

    if (clientChannels.length > 1 && !isKitchenProduct && !composition.isIngredient) {
      const tooltipChannelNames = clientChannels
        .map((channel) =>
          i18next.t('ADMIN.RECIPES.PRICE_COST_RATIO_MULTIPLE_CHANNELS_NAMES_TOOLTIP', {
            channelName: i18next.t(`${channel.translationKey}.FULL`),
          }),
        )
        .join(' | ');

      const tooltipCalculations = i18next.t(
        'ADMIN.RECIPES.PRICE_COST_RATIO_MULTIPLE_CHANNELS_CALCULATIONS_TOOLTIP',
      );

      return {
        tooltip: `${tooltipChannelNames} - ${tooltipCalculations}`,
        value,
      };
    }

    return {
      tooltip: i18next.t('ADMIN.RECIPES.COMPOSITION_COST_DETAIL_RATIO'),
      value: value,
    };
  };

  // Margin ratio tooltip and value
  const getMarginRatioValue = (currentProduct) => {
    const { marginRatioByChannelId } = currentProduct;

    if (!marginRatioByChannelId) {
      return '-';
    }

    if (isKitchenProduct || clientChannels.length === 1) {
      return `${formatNumber(marginRatioByChannelId[defaultChannelId], numberDecimals)}%`;
    }

    if (clientChannels.length > 1 && !composition.isIngredient) {
      return clientChannels
        .map((channel) => {
          const ratio = marginRatioByChannelId[channel.id];

          return `${formatNumber(ratio, numberDecimals)}%`;
        })
        .join(' | ');
    }

    if (marginRatioByChannelId[defaultChannelId]) {
      return `${formatNumber(marginRatioByChannelId[defaultChannelId], numberDecimals)}%`;
    }

    return '-';
  };

  const getMarginRatioToolTipAndValue = () => {
    const { marginRatioByChannelId } = product;

    const value = getMarginRatioValue(product);

    if (marginRatioByChannelId && clientChannels.length > 1 && !composition.isIngredient) {
      const tooltipChannelNames = clientChannels
        .map((channel) =>
          i18next.t('ADMIN.PRODUCTS.COMPOSITION_MARGIN_RATIO_MULTI_CHANNEL_NAMES_TOOLTIP', {
            channelName: i18next.t(`${channel.translationKey}.FULL`),
          }),
        )
        .join(' | ');

      const tooltipCalculations = i18next.t(
        'ADMIN.PRODUCTS.COMPOSITION_MARGIN_RATIO_MULTI_CHANNEL_CALCULATIONS_TOOLTIP',
      );
      const tooltip = `${tooltipChannelNames} - ${tooltipCalculations}`;

      return {
        tooltip,
        value,
      };
    }

    return {
      tooltip: i18next.t('ADMIN.PRODUCTS.COMPOSITION_MARGIN_RATIO'),
      value,
    };
  };

  const withMainRecipeInfo = (tooltip) => {
    if (!hasRecipeVariant || composition.isIngredient) {
      return tooltip;
    }

    const mainRecipeInfo = i18next.t('ADMIN.PRODUCTS.COMPOSITION_MAIN_RECIPE_INFO_TOOLTIP');

    if (!tooltip) {
      return mainRecipeInfo;
    }

    return `${tooltip} ${mainRecipeInfo}`;
  };

  const { tooltip: costExclVATTooltip, value: costExclVATValue } = getCostExclVATToolTipAndValue();
  const { tooltip: priceExclVATTooltip, value: priceExclVATValue } =
    getPriceExclVATToolTipAndValue();
  const { tooltip: ratioTooltip, value: ratioValue } = getRatioToolTipAndValue();
  const { tooltip: marginRatioTooltip, value: marginRatioValue } = getMarginRatioToolTipAndValue();

  return (
    <Container compositionError={compositionError}>
      <TitleContainer>
        <Text font={ENUM_FONTS.H2}>{i18next.t('ADMIN.PRODUCTS.COMPOSITION')}</Text>
        {showIngredientOrRecipeDropdown && !isEmpty(composition) && isKitchenProduct && (
          <ExternalRedirectionContainer
            // hard set the marginBottom so the link stays level with the dropdowns instead being lowered because of the warning message
            style={{ margin: '0px', alignSelf: 'center' }}
            onClick={handleOpenEntityDetails}
          >
            <LinkImage src="/images/inpulse/open-a-new-black-small.svg" />
            <Text font={ENUM_FONTS.TEXT_MIDDLE_BOLD}>
              {selectedCompoType.id === 'RECIPE'
                ? i18next.t('ADMIN.PRODUCTS.SEE_RECIPE')
                : i18next.t('ADMIN.PRODUCTS.SEE_INGREDIENT')}
            </Text>
          </ExternalRedirectionContainer>
        )}
      </TitleContainer>
      <DropdownsContainer>
        <Dropdown
          customStyle={{ height: 'auto' }}
          isDisabled={isReadOnly}
          isRequired={true}
          items={
            !isKitchenProduct
              ? compositionTypes
              : compositionTypes.filter(({ id }) => id !== 'NONE')
          }
          label={`${i18next.t('ADMIN.PRODUCTS.COMPOSITION_TYPE')} :`}
          labelStyle={{ font: theme.fonts.textSmall }}
          placeholder={i18next.t('GENERAL.SEARCH')}
          selectedItem={selectedCompoType}
          shouldInterpretTooltipHTML={true}
          tooltipText={i18next.t('ADMIN.PRODUCTS.COMPOSITION_TYPE_TOOLTIP')}
          onSelectionChange={handleCompositionTypeChange}
        />
        {showIngredientOrRecipeDropdown && (
          <CompositionContainer>
            {selectedCompoType.id === 'RECIPE' ? (
              <RecipeDropdown
                composition={composition}
                handleEntityChange={handleEntityChange}
                isLoading={isLoading}
                isReadOnly={isReadOnly}
                recipes={recipes}
                warningMessage={warningMessage}
                width={isKitchenProduct ? '240px' : '340px'}
              />
            ) : (
              <IngredientDropdown
                composition={composition}
                handleEntityChange={handleEntityChange}
                handleIngredientCreation={() => setOpenCreateIngredientModal(true)}
                ingredients={ingredients}
                isKitchenProduct={isKitchenProduct}
                isLoading={isLoading}
                isReadOnly={isReadOnly}
                width={isKitchenProduct ? '240px' : '340px'}
              />
            )}
            <Input
              label={i18next.t('ADMIN.PRODUCTS.PORTION_SIZE_IN_GRAMS')}
              type="number"
              value={portionSizeInGrams}
              width={240}
              onChange={(e) => handlePortionSizeInGramsChange(e.target.value)}
            />
            {!isEmpty(composition) && !isKitchenProduct && (
              <ExternalRedirectionContainer
                // hard set the marginBottom so the link stays level with the dropdowns instead being lowered because of the warning message
                style={warningMessage ? { marginBottom: '62px' } : {}}
                onClick={handleOpenEntityDetails}
              >
                <LinkImage src="/images/inpulse/open-a-new-black-small.svg" />
                <Text font={ENUM_FONTS.TEXT_MIDDLE_BOLD}>
                  {selectedCompoType.id === 'RECIPE'
                    ? i18next.t('ADMIN.PRODUCTS.SEE_RECIPE')
                    : i18next.t('ADMIN.PRODUCTS.SEE_INGREDIENT')}
                </Text>
              </ExternalRedirectionContainer>
            )}
          </CompositionContainer>
        )}
      </DropdownsContainer>
      {!isEmpty(composition) && (
        <InformationComponentsContainer>
          <InformationComponent
            label={i18next.t('ADMIN.PRODUCTS.COST_EXCL_TAXES_WITH_CURRENCY', {
              alphabeticCode: alphabeticCode,
            })}
            tooltipText={withMainRecipeInfo(costExclVATTooltip)}
            value={costExclVATValue}
          />
          <GapContainer>
            <InformationComponent
              label={i18next.t('ADMIN.PRODUCTS.PRICE_EXCL_TAXES_WITH_CURRENCY', {
                alphabeticCode: alphabeticCode,
              })}
              tooltipText={withMainRecipeInfo(priceExclVATTooltip)}
              value={priceExclVATValue}
            />
          </GapContainer>
          <InformationComponent
            label={i18next.t('ADMIN.PRODUCTS.RATIO')}
            tooltipText={withMainRecipeInfo(ratioTooltip)}
            value={ratioValue}
          />
          <GapContainer>
            <InformationComponent
              label={i18next.t('GENERAL.MARGIN')}
              tooltipText={withMainRecipeInfo(marginRatioTooltip)}
              value={marginRatioValue}
            />
          </GapContainer>
        </InformationComponentsContainer>
      )}
      {!!openCreateIngredientModal && isKitchenProduct && (
        <CreateIngredientModal
          handleCallToCreateIngredient={handleCallToCreateIngredient}
          resetIngredientCreation={() => {
            setOpenCreateIngredientModal(false);
          }}
        />
      )}
    </Container>
  );
};

const mapStateToProps = (state) => ({
  client: getClientInfo(state.baseReducer.user),
  currency: state.baseReducer.currency,
});

const mapDispatchToProps = (dispatch) => ({
  showSuccessMessage: (message) => {
    dispatch(showSuccessMessage(message));
  },
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  pageLoading: () => {
    dispatch(loading());
  },
  pageLoaded: () => {
    dispatch(loadingSuccess());
  },
  closeGenericModal: (params) => {
    dispatch(closeGenericModal(params));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductComposition);
